import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    CpmButton,
    CpmFilterDropdown,
    CpmInlineErrorBanner,
    type CpmFilterOption,
} from '../../../../components/design-system';
import {
    CPM_EXCISE_CATEGORY_MAX_LENGTH,
    CPM_EXCISE_MAX_STANDARD_RANKS,
    CPM_EXCISE_PACK_COPY,
    CPM_EXCISE_RANK_HINTS,
    CPM_EXCISE_RANK_LABELS,
    CPM_VOLUME_DEFAULT_UNIT,
    cpmExcisePackKey,
    cpmExciseRankFailCopy,
    cpmExciseReadingStamp,
    cpmExciseRefreshFailCopy,
    cpmExciseSeenEmptyCopy,
    cpmExciseSeenHeading,
    cpmExciseSeenNote,
    cpmExciseStaleOptionLabel,
    cpmExciseStalePillTitle,
    formatCpmCompactRankMl,
    formatCpmCount,
    formatCpmPackMl,
    formatCpmRankChainMl,
    parseCpmExcisePackKey,
} from '../../../../constants/centralProductMaster.constants';
import type {
    CpmExciseCategory,
    CpmExcisePackVolume,
    CpmExciseStandardVolume,
} from '../../../../types/centralProductMaster.types';
import { getApiErrorMessage } from '../../../../utils/api/ApiClient';
import SubCategoryEditor from './SubCategoryEditor';

type CardPhase = 'idle' | 'saving' | 'error';
type RankSlots = [string, string, string];

interface CategoryCardProps {
    category: CpmExciseCategory;
    stateName: string;
    expanded: boolean;
    showChips: boolean;
    busy?: boolean;
    onToggle: () => void;
    onRename: (name: string) => Promise<void> | void;
    onToggleStatus: () => Promise<void> | void;
    onAskDelete: () => void;
    onAddSub: (name: string) => Promise<void> | void;
    onRenameSub: (subId: string, name: string) => Promise<void> | void;
    onRemoveSub: (subId: string) => Promise<void> | void;
    onRefreshSizes: (cat: CpmExciseCategory) => Promise<void>;
    onSetStandardVolumes: (
        cat: CpmExciseCategory,
        volumes: CpmExciseStandardVolume[]
    ) => Promise<void>;
}

function emptySlots(): RankSlots {
    return ['', '', ''];
}

function sortedSeen(seen: CpmExcisePackVolume[] | undefined): CpmExcisePackVolume[] {
    return [...(seen ?? [])].sort((a, b) => a.volumeMl - b.volumeMl);
}

function sortedRanks(ranks: CpmExciseStandardVolume[] | undefined): CpmExciseStandardVolume[] {
    return [...(ranks ?? [])].sort((a, b) => a.rank - b.rank);
}

function slotsFromRanks(ranks: CpmExciseStandardVolume[] | undefined): RankSlots {
    const next = emptySlots();
    for (const row of sortedRanks(ranks)) {
        if (row.rank >= 1 && row.rank <= CPM_EXCISE_MAX_STANDARD_RANKS) {
            next[row.rank - 1] = cpmExcisePackKey(row.volumeMl, row.volumeUnit);
        }
    }
    return next;
}

function compactifySlots(slots: RankSlots, seen: CpmExcisePackVolume[]): CpmExciseStandardVolume[] {
    const seenByKey = new Map(seen.map((s) => [cpmExcisePackKey(s.volumeMl, s.volumeUnit), s]));
    const kept: CpmExcisePackVolume[] = [];
    for (const key of slots) {
        if (!key) continue;
        const row = seenByKey.get(key);
        if (!row) continue;
        kept.push(row);
    }
    return kept.map((row, i) => ({
        volumeMl: row.volumeMl,
        volumeUnit: row.volumeUnit || CPM_VOLUME_DEFAULT_UNIT,
        rank: i + 1,
    }));
}

function seenKeySet(seen: CpmExcisePackVolume[]): Set<string> {
    return new Set(seen.map((s) => cpmExcisePackKey(s.volumeMl, s.volumeUnit)));
}

function savedRankText(ranks: CpmExciseStandardVolume[]): string | null {
    if (ranks.length === 0) return null;
    return formatCpmCompactRankMl(ranks);
}

function usageHref(stateName: string, categoryId: string): string {
    return `/central-product-master/master-editor?level=stateProducts&state=${encodeURIComponent(stateName)}&exciseCategoryId=${encodeURIComponent(categoryId)}`;
}

export default function CategoryCard({
    category,
    stateName,
    expanded,
    showChips,
    busy = false,
    onToggle,
    onRename,
    onToggleStatus,
    onAskDelete,
    onAddSub,
    onRenameSub,
    onRemoveSub,
    onRefreshSizes,
    onSetStandardVolumes,
}: CategoryCardProps) {
    const [renaming, setRenaming] = useState(false);
    const [renameVal, setRenameVal] = useState(category.category);
    const [szPhase, setSzPhase] = useState<CardPhase>('idle');
    const [rkPhase, setRkPhase] = useState<CardPhase>('idle');
    const [rkError, setRkError] = useState<string | null>(null);
    const [slots, setSlots] = useState<RankSlots>(() => slotsFromRanks(category.standardVolumes));
    const [tipOpen, setTipOpen] = useState(false);

    const active = (category.status || 'ACTIVE') === 'ACTIVE';
    const usage = category.usageCount ?? 0;
    const subs = category.subCategories ?? [];
    const href = usageHref(stateName, category._id);

    const seen = useMemo(() => sortedSeen(category.seenVolumes), [category.seenVolumes]);
    const savedRanks = useMemo(
        () => sortedRanks(category.standardVolumes),
        [category.standardVolumes]
    );
    const seenKeys = useMemo(() => seenKeySet(seen), [seen]);
    const rankByKey = useMemo(() => {
        const map = new Map<string, number>();
        for (const row of savedRanks) {
            map.set(cpmExcisePackKey(row.volumeMl, row.volumeUnit), row.rank);
        }
        return map;
    }, [savedRanks]);

    const ranksSig = savedRanks.map((r) => `${r.rank}:${r.volumeMl}:${r.volumeUnit}`).join('|');

    useEffect(() => {
        if (rkPhase !== 'idle') return;
        setSlots(slotsFromRanks(category.standardVolumes));
    }, [category._id, ranksSig, rkPhase, category.standardVolumes]);

    const staleSaved = useMemo(
        () => savedRanks.filter((r) => !seenKeys.has(cpmExcisePackKey(r.volumeMl, r.volumeUnit))),
        [savedRanks, seenKeys]
    );
    const slotStale = slots.some((key) => Boolean(key) && !seenKeys.has(key));

    const hint = useMemo(() => {
        if (savedRanks.length > 0) {
            const rankText = formatCpmCompactRankMl(savedRanks);
            if (staleSaved.length > 0) {
                return {
                    text: `${rankText}${CPM_EXCISE_PACK_COPY.RE_PICK_SUFFIX}`,
                    tone: 'warn' as const,
                    title: staleSaved
                        .map((s) => cpmExciseStalePillTitle(s.volumeMl, s.volumeUnit))
                        .join('\n'),
                };
            }
            return { text: rankText, tone: 'default' as const, title: undefined };
        }
        if (seen.length > 0) {
            return {
                text: CPM_EXCISE_PACK_COPY.NO_STANDARD_PACK,
                tone: 'warn' as const,
                title: undefined,
            };
        }
        return {
            text: CPM_EXCISE_PACK_COPY.NO_SIZES_SEEN,
            tone: 'muted' as const,
            title: undefined,
        };
    }, [savedRanks, seen.length, staleSaved]);

    const renameNote =
        usage > 0
            ? `Renaming updates the name on ${formatCpmCount(usage)} linked StateProduct rows automatically.`
            : 'Nothing is linked yet, so this rename affects the taxonomy only.';

    const ranksBusy = rkPhase === 'saving';
    const ranksBlocked = seen.length === 0;
    const selectsDisabled = ranksBusy || ranksBlocked;
    const ranksSummary =
        staleSaved.length > 0
            ? CPM_EXCISE_PACK_COPY.STALE_SUMMARY
            : savedRanks.length > 0
              ? formatCpmRankChainMl(savedRanks)
              : CPM_EXCISE_PACK_COPY.OPTIONAL_NONE_SET;

    const refreshIdle = szPhase === 'idle';
    const refreshStyle = {
        gap: 7,
        height: 30,
        padding: '0 12px',
        fontSize: 12,
        fontWeight: 800,
        cursor: szPhase === 'saving' ? 'progress' : 'pointer',
        borderRadius: 9,
        border: `1.5px solid ${
            szPhase === 'error' ? 'var(--cpm-shortfall-border)' : 'var(--cpm-border-strong)'
        }`,
        background: szPhase === 'saving' ? 'var(--cpm-purple-tint-7)' : '#fff',
        color:
            szPhase === 'error'
                ? 'var(--cpm-shortfall-text)'
                : szPhase === 'saving'
                  ? 'var(--cpm-brand-purple)'
                  : 'var(--cpm-text-secondary)',
    } as const;

    const handleRefreshSizesClick = async () => {
        setSzPhase('saving');
        try {
            await onRefreshSizes(category);
            setSzPhase('idle');
        } catch {
            setSzPhase('error');
        }
    };

    const submitRanks = async (next: RankSlots) => {
        setRkPhase('saving');
        try {
            await onSetStandardVolumes(category, compactifySlots(next, seen));
            setRkError(null);
            setRkPhase('idle');
        } catch (err) {
            setRkError(getApiErrorMessage(err));
            setRkPhase('error');
        }
    };

    const handleRankChange = async (slotIndex: number, nextKey: string) => {
        const next: RankSlots = [slots[0], slots[1], slots[2]];
        next[slotIndex] = nextKey;
        setSlots(next);
        await submitRanks(next);
    };

    const refreshLabel =
        szPhase === 'saving'
            ? CPM_EXCISE_PACK_COPY.REFRESH_IN_FLIGHT
            : szPhase === 'error'
              ? CPM_EXCISE_PACK_COPY.REFRESH_RETRY
              : CPM_EXCISE_PACK_COPY.REFRESH_IDLE;

    const hintToneStyle =
        hint.tone === 'warn'
            ? {
                  background: 'var(--cpm-warning-bg)',
                  border: '1px solid var(--cpm-warning-border)',
                  color: 'var(--cpm-warning-text)',
              }
            : hint.tone === 'muted'
              ? {
                    background: 'var(--cpm-surface-subtle)',
                    border: '1px solid var(--cpm-border-default)',
                    color: 'var(--cpm-text-tertiary)',
                }
              : {
                    background: 'var(--cpm-purple-tint-13)',
                    border: '1px solid var(--cpm-purple-accent-border)',
                    color: 'var(--cpm-brand-purple)',
                };

    return (
        <div
            className="rounded-[12px] bg-[var(--cpm-surface-card)] overflow-hidden transition-[border-color] duration-150"
            style={{
                gridColumn: expanded ? '1 / -1' : 'auto',
                border: `1px solid ${expanded ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-default)'}`,
                boxShadow: expanded
                    ? '0 1px 2px rgba(14,8,88,.04), 0 14px 34px -14px rgba(14,8,88,.22)'
                    : '0 1px 2px rgba(14,8,88,.04)',
            }}
        >
            <div className="flex flex-col" style={{ gap: 11, padding: '14px 16px' }}>
                <div className="flex items-center" style={{ gap: 11 }}>
                    <button
                        type="button"
                        onClick={onToggle}
                        className="shrink-0 inline-flex items-center justify-center rounded-[8px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-text-secondary)]"
                        style={{
                            width: 28,
                            height: 28,
                            cursor: 'pointer',
                            transition: 'transform 150ms ease',
                            transform: `rotate(${expanded ? '90deg' : '0deg'})`,
                        }}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                        >
                            <path d="m9 18 6-6-6-6" />
                        </svg>
                    </button>

                    {renaming ? (
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 7 }}>
                            <div className="flex items-center" style={{ gap: 8 }}>
                                <input
                                    value={renameVal}
                                    onChange={(e) => setRenameVal(e.target.value)}
                                    maxLength={CPM_EXCISE_CATEGORY_MAX_LENGTH}
                                    placeholder="Category name as the department writes it"
                                    className="flex-1 min-w-0 rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-brand-navy)]"
                                    style={{
                                        height: 38,
                                        padding: '0 12px',
                                        fontSize: 14,
                                        fontWeight: 800,
                                    }}
                                    autoFocus
                                />
                                <CpmButton
                                    variant="navy"
                                    size="sm"
                                    disabled={busy || !renameVal.trim()}
                                    onClick={async () => {
                                        const v = renameVal.trim();
                                        if (!v) return;
                                        await onRename(v);
                                        setRenaming(false);
                                    }}
                                >
                                    Save
                                </CpmButton>
                                <CpmButton
                                    variant="outline"
                                    size="sm"
                                    disabled={busy}
                                    onClick={() => setRenaming(false)}
                                >
                                    Cancel
                                </CpmButton>
                            </div>
                            <span
                                className="inline-flex items-center text-[var(--cpm-info-text)]"
                                style={{ gap: 7, fontSize: 11.5, fontWeight: 600 }}
                            >
                                <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--cpm-info-icon)"
                                    strokeWidth="2"
                                    className="shrink-0"
                                >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 16v-4" />
                                    <path d="M12 8h.01" />
                                </svg>
                                {renameNote}
                            </span>
                        </div>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={onToggle}
                                className="flex-1 min-w-0 truncate text-left bg-transparent border-none p-0"
                                style={{
                                    fontSize: 15,
                                    fontWeight: 800,
                                    letterSpacing: '-0.01em',
                                    cursor: 'pointer',
                                    color: active
                                        ? 'var(--cpm-text-primary)'
                                        : 'var(--cpm-text-tertiary)',
                                    textDecoration: active ? 'none' : 'line-through',
                                }}
                            >
                                {category.category}
                            </button>
                            <span
                                className="shrink-0 inline-flex items-center rounded-full"
                                style={{
                                    gap: 6,
                                    padding: '4px 10px',
                                    fontSize: 11.5,
                                    fontWeight: 800,
                                    background: active ? 'var(--cpm-success-bg)' : '#fff',
                                    border: `1px solid ${active ? 'var(--cpm-success-border)' : 'var(--cpm-border-strong)'}`,
                                    color: active
                                        ? 'var(--cpm-success-text-light)'
                                        : 'var(--cpm-text-secondary)',
                                }}
                            >
                                <span
                                    className="rounded-full"
                                    style={{
                                        width: 7,
                                        height: 7,
                                        background: active
                                            ? 'var(--cpm-success-dot)'
                                            : 'var(--cpm-text-tertiary)',
                                    }}
                                />
                                {active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                            <span
                                className="shrink-0 inline-flex items-center rounded-full whitespace-nowrap"
                                title={hint.title}
                                style={{
                                    gap: 6,
                                    padding: '4px 10px',
                                    fontSize: 11.5,
                                    fontWeight: 800,
                                    ...hintToneStyle,
                                }}
                            >
                                {hint.text}
                            </span>
                            <Link
                                to={href}
                                className={`!shrink-0 !text-right !tabular-nums !no-underline !font-extrabold ${
                                    usage > 0
                                        ? '!text-[var(--cpm-text-secondary)]'
                                        : '!text-[var(--cpm-text-tertiary)]'
                                }`}
                                style={{ fontSize: 12 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {usage > 0 ? `${formatCpmCount(usage)} products` : 'No products'}
                            </Link>
                        </>
                    )}
                </div>

                {showChips && !renaming ? (
                    <div className="flex flex-wrap" style={{ gap: 6, paddingLeft: 39 }}>
                        {subs.length > 0 ? (
                            subs.map((s) => (
                                <span
                                    key={s._id}
                                    className="inline-flex items-center rounded-full bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                                    style={{
                                        padding: '3px 9px',
                                        fontSize: 11.5,
                                        fontWeight: 700,
                                    }}
                                >
                                    {s.name}
                                </span>
                            ))
                        ) : (
                            <span
                                className="inline-flex items-center rounded-full bg-[var(--cpm-surface-subtle)] text-[var(--cpm-text-tertiary)]"
                                style={{
                                    padding: '3px 9px',
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                }}
                            >
                                No subcategories
                            </span>
                        )}
                    </div>
                ) : null}
            </div>

            {expanded ? (
                <div
                    className="border-t border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)] flex flex-col overflow-visible rounded-b-[11px]"
                    style={{ padding: '16px 18px 18px', gap: 14 }}
                >
                    <SubCategoryEditor
                        subs={subs}
                        busy={busy}
                        onAdd={onAddSub}
                        onRename={onRenameSub}
                        onRemove={onRemoveSub}
                    />

                    <div
                        className="flex flex-col min-w-0 border-t border-[var(--cpm-border-default)]"
                        style={{ gap: 12, paddingTop: 13 }}
                    >
                    <div className="flex flex-col min-w-0" style={{ gap: 9 }}>
                        <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                            <span
                                className="uppercase text-[var(--cpm-text-secondary)]"
                                style={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    letterSpacing: '0.08em',
                                }}
                            >
                                {cpmExciseSeenHeading(seen.length)}
                            </span>
                            <button
                                type="button"
                                disabled={szPhase === 'saving'}
                                onClick={handleRefreshSizesClick}
                                className="inline-flex items-center disabled:opacity-80"
                                style={refreshStyle}
                            >
                                {szPhase === 'saving' ? (
                                    <span
                                        className="cpm-spin"
                                        style={{ width: 12, height: 12, borderWidth: 1.8 }}
                                    />
                                ) : refreshIdle ? (
                                    <svg
                                        width="13"
                                        height="13"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                        <path d="M3 3v5h5" />
                                    </svg>
                                ) : null}
                                {refreshLabel}
                            </button>
                            {szPhase === 'saving' ? (
                                <span
                                    className="text-[var(--cpm-text-tertiary)]"
                                    style={{ fontSize: 11.5, fontWeight: 700 }}
                                    aria-live="polite"
                                >
                                    {cpmExciseReadingStamp(stateName)}
                                </span>
                            ) : null}
                        </div>

                        {szPhase === 'error' ? (
                            <CpmInlineErrorBanner
                                compact
                                message={cpmExciseRefreshFailCopy(category.category)}
                                onRetry={handleRefreshSizesClick}
                                retryLabel={CPM_EXCISE_PACK_COPY.RANK_RETRY}
                            />
                        ) : null}

                        {seen.length > 0 ? (
                            <div className="flex flex-wrap" style={{ gap: 6 }}>
                                {seen.map((pack) => {
                                    const key = cpmExcisePackKey(pack.volumeMl, pack.volumeUnit);
                                    const rank = rankByKey.get(key);
                                    const ranked = rank === 1 || rank === 2 || rank === 3;
                                    return (
                                        <span
                                            key={key}
                                            className="inline-flex items-center rounded-full tabular-nums"
                                            style={{
                                                gap: 6,
                                                padding: '4px 10px',
                                                fontSize: 12,
                                                fontWeight: 800,
                                                background: ranked
                                                    ? 'var(--cpm-purple-tint-7)'
                                                    : '#fff',
                                                border: `1px solid ${
                                                    ranked
                                                        ? 'var(--cpm-purple-accent-border)'
                                                        : 'var(--cpm-border-default)'
                                                }`,
                                                color: ranked
                                                    ? 'var(--cpm-brand-navy)'
                                                    : 'var(--cpm-text-primary)',
                                            }}
                                        >
                                            {formatCpmPackMl(pack.volumeMl, pack.volumeUnit)}
                                            {ranked ? (
                                                <span
                                                    className="uppercase"
                                                    style={{
                                                        fontSize: 9.5,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.06em',
                                                        color: 'var(--cpm-brand-purple)',
                                                    }}
                                                >
                                                    {CPM_EXCISE_RANK_LABELS[rank - 1]}
                                                </span>
                                            ) : null}
                                        </span>
                                    );
                                })}
                            </div>
                        ) : (
                            <span
                                className="text-[var(--cpm-text-secondary)]"
                                style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}
                            >
                                {cpmExciseSeenEmptyCopy(stateName, category.category)}
                            </span>
                        )}

                        {seen.length > 0 ? (
                            <span
                                className="text-[var(--cpm-text-tertiary)]"
                                style={{ fontSize: 11.5, fontWeight: 600, lineHeight: 1.45 }}
                            >
                                {cpmExciseSeenNote(stateName, category.category)}
                            </span>
                        ) : null}
                    </div>

                    <div
                        className="flex flex-col min-w-0 rounded-[10px] border border-[var(--cpm-border-default)] bg-[var(--cpm-purple-tint-5)]"
                        style={{ gap: 11, padding: '13px 14px' }}
                    >
                        <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                            <span
                                className="uppercase text-[var(--cpm-brand-navy)]"
                                style={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    letterSpacing: '0.08em',
                                }}
                            >
                                {CPM_EXCISE_PACK_COPY.STANDARD_PACK}
                            </span>
                            <span
                                className="relative inline-flex items-center justify-center rounded-full bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                                aria-label={CPM_EXCISE_PACK_COPY.WHY_RANKS_TITLE}
                                onMouseEnter={() => setTipOpen(true)}
                                onMouseLeave={() => setTipOpen(false)}
                                onClick={() => setTipOpen(true)}
                                style={{ width: 17, height: 17, cursor: 'default' }}
                            >
                                <svg
                                    width="11"
                                    height="11"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 16v-4" />
                                    <path d="M12 8h.01" />
                                </svg>
                                {tipOpen ? (
                                    <span
                                        role="tooltip"
                                        className="pointer-events-none absolute z-40 flex flex-col text-left"
                                        style={{
                                            bottom: 'calc(100% + 10px)',
                                            left: -12,
                                            width: 280,
                                            gap: 7,
                                            padding: '12px 13px',
                                            borderRadius: 12,
                                            background: '#fff',
                                            border: '1px solid var(--cpm-border-strong)',
                                            boxShadow: 'var(--cpm-shadow-popover)',
                                            letterSpacing: 0,
                                        }}
                                    >
                                        <span className="inline-flex items-center" style={{ gap: 7 }}>
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="var(--cpm-brand-purple)"
                                                strokeWidth="2.4"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="shrink-0"
                                            >
                                                <path d="M3 6h18" />
                                                <path d="M19 6v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                                                <path d="M10 11h4" />
                                            </svg>
                                            <span
                                                className="uppercase text-[var(--cpm-brand-navy)]"
                                                style={{
                                                    fontSize: 10,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.07em',
                                                }}
                                            >
                                                {CPM_EXCISE_PACK_COPY.WHY_RANKS_TITLE}
                                            </span>
                                        </span>
                                        <span
                                            className="text-[var(--cpm-text-primary)]"
                                            style={{
                                                fontSize: 11.5,
                                                fontWeight: 600,
                                                lineHeight: 1.5,
                                                textWrap: 'pretty',
                                            }}
                                        >
                                            {CPM_EXCISE_PACK_COPY.WHY_RANKS_BODY}
                                        </span>
                                        <span
                                            className="inline-flex items-center rounded-[9px] bg-[var(--cpm-purple-tint-7)] border border-[var(--cpm-purple-accent-border)]"
                                            style={{ gap: 6, padding: '7px 9px' }}
                                        >
                                            <span
                                                className="shrink-0 rounded-full bg-white border border-[var(--cpm-purple-accent-border)] text-[var(--cpm-brand-navy)] tabular-nums"
                                                style={{
                                                    padding: '2px 7px',
                                                    fontSize: 10.5,
                                                    fontWeight: 800,
                                                }}
                                            >
                                                {CPM_EXCISE_PACK_COPY.WHY_RANKS_EXAMPLE_SIZE}
                                            </span>
                                            <span
                                                className="text-[var(--cpm-text-secondary)]"
                                                style={{ fontSize: 10.5, fontWeight: 700 }}
                                            >
                                                {CPM_EXCISE_PACK_COPY.WHY_RANKS_EXAMPLE}
                                            </span>
                                        </span>
                                    </span>
                                ) : null}
                            </span>
                            <span
                                className="text-[var(--cpm-text-secondary)]"
                                style={{ fontSize: 11.5, fontWeight: 700 }}
                            >
                                {ranksSummary}
                            </span>
                            {ranksBusy ? (
                                <span
                                    className="ml-auto inline-flex items-center text-[var(--cpm-brand-purple)]"
                                    style={{ gap: 7, fontSize: 11.5, fontWeight: 800 }}
                                    aria-live="polite"
                                >
                                    <span
                                        className="cpm-spin"
                                        style={{ width: 12, height: 12, borderWidth: 1.8 }}
                                    />
                                    {CPM_EXCISE_PACK_COPY.RANK_SAVING}
                                </span>
                            ) : null}
                        </div>

                        {rkPhase === 'error' ? (
                            <CpmInlineErrorBanner
                                compact
                                message={cpmExciseRankFailCopy(
                                    category.category,
                                    savedRankText(savedRanks),
                                    rkError
                                )}
                                onRetry={() => submitRanks(slots)}
                                retryLabel={CPM_EXCISE_PACK_COPY.RANK_RETRY}
                            />
                        ) : null}

                        {ranksBlocked ? (
                            <span
                                className="text-[var(--cpm-text-secondary)]"
                                style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}
                            >
                                {CPM_EXCISE_PACK_COPY.BLOCKED_RANKS}
                            </span>
                        ) : null}

                        {slotStale && !ranksBlocked ? (
                            <span
                                className="text-[var(--cpm-warning-text)]"
                                style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.45 }}
                            >
                                {CPM_EXCISE_PACK_COPY.STALE_SAVE_HINT}
                            </span>
                        ) : null}

                        {ranksBlocked ? null : (
                        <div
                            className="grid"
                            style={{
                                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                gap: 10,
                                alignItems: 'start',
                            }}
                        >
                            {CPM_EXCISE_RANK_LABELS.map((label, slotIndex) => {
                                const currentKey = slots[slotIndex] ?? '';
                                const taken = new Set(
                                    slots.filter((key, i) => key && i !== slotIndex)
                                );
                                const options: CpmFilterOption[] = [];
                                for (const pack of seen) {
                                    const key = cpmExcisePackKey(pack.volumeMl, pack.volumeUnit);
                                    if (taken.has(key)) continue;
                                    options.push({
                                        value: key,
                                        label: formatCpmPackMl(pack.volumeMl, pack.volumeUnit),
                                    });
                                }
                                if (
                                    currentKey &&
                                    !seenKeys.has(currentKey) &&
                                    !taken.has(currentKey)
                                ) {
                                    const parsed = parseCpmExcisePackKey(currentKey);
                                    if (parsed) {
                                        options.push({
                                            value: currentKey,
                                            label: cpmExciseStaleOptionLabel(
                                                parsed.volumeMl,
                                                parsed.volumeUnit
                                            ),
                                        });
                                    }
                                }
                                const currentStale =
                                    Boolean(currentKey) && !seenKeys.has(currentKey);
                                return (
                                    <label
                                        key={label}
                                        className="flex flex-col min-w-0"
                                        style={{ gap: 5 }}
                                    >
                                        <span
                                            className="uppercase text-[var(--cpm-text-secondary)]"
                                            style={{
                                                fontSize: 10,
                                                fontWeight: 800,
                                                letterSpacing: '0.08em',
                                            }}
                                        >
                                            {label}
                                        </span>
                                        <CpmFilterDropdown
                                            multi={false}
                                            label=""
                                            includeEmptyOption
                                            emptyLabel={CPM_EXCISE_PACK_COPY.NOT_SET}
                                            portal
                                            disabled={selectsDisabled}
                                            error={currentStale}
                                            highlightSelected={Boolean(currentKey)}
                                            className="!max-w-none !min-w-0 flex-none w-full"
                                            options={options}
                                            selected={currentKey ? [currentKey] : []}
                                            onChange={(vals) => {
                                                void handleRankChange(slotIndex, vals[0] ?? '');
                                            }}
                                        />
                                        <span
                                            className="text-[var(--cpm-text-tertiary)]"
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {CPM_EXCISE_RANK_HINTS[slotIndex]}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                        )}
                    </div>
                    </div>

                    <div
                        className="flex items-center flex-wrap border-t border-[var(--cpm-border-default)]"
                        style={{ gap: 10, paddingTop: 13 }}
                    >
                        <CpmButton
                            variant="outline"
                            size="sm"
                            disabled={busy}
                            onClick={() => {
                                setRenaming(true);
                                setRenameVal(category.category);
                            }}
                            leadingIcon={
                                <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M12 20h9" />
                                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                                </svg>
                            }
                        >
                            Rename category
                        </CpmButton>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => onToggleStatus()}
                            className="inline-flex items-center rounded-[10px] disabled:opacity-50"
                            style={{
                                gap: 7,
                                height: 34,
                                padding: '0 13px',
                                fontSize: 12.5,
                                fontWeight: 800,
                                cursor: 'pointer',
                                border: `1.5px solid ${active ? 'var(--cpm-warning-border)' : 'var(--cpm-success-border)'}`,
                                background: active
                                    ? 'var(--cpm-warning-bg)'
                                    : 'var(--cpm-success-bg)',
                                color: active
                                    ? 'var(--cpm-warning-text)'
                                    : 'var(--cpm-success-text-light)',
                            }}
                        >
                            {active ? 'Disable category' : 'Enable category'}
                        </button>
                        <CpmButton
                            variant="outline"
                            size="sm"
                            disabled={busy}
                            onClick={onAskDelete}
                            className="hover:!border-[var(--cpm-shortfall-text)] hover:!text-[var(--cpm-shortfall-text)]"
                            leadingIcon={
                                <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M3 6h18" />
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                    <path d="M10 11v6" />
                                    <path d="M14 11v6" />
                                </svg>
                            }
                        >
                            Delete
                        </CpmButton>
                        <Link
                            to={href}
                            className="!ml-auto !tabular-nums !no-underline !font-bold !text-[var(--cpm-text-tertiary)] hover:!text-[var(--cpm-brand-purple)]"
                            style={{ fontSize: 11.5 }}
                        >
                            {usage > 0
                                ? `${formatCpmCount(usage)} StateProduct rows reference this category`
                                : 'No StateProduct rows reference this category'}
                        </Link>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
